// LeadInsuranceProfilePanel — Phase 2 of the Lead Command Center.
//
// Three-tab insurance profile: Health / Life / Medicare. Each tab
// hosts the fields an agent actually needs before opening the
// conversation.
//
// Persistence: the lead already carries arbitrary fields via
// userStorage / cloudBackup, so we hang everything off
// lead.insurance_profile.{health,life,medicare} and dispatch the
// existing onEdit callback with the mutated lead. No schema change
// needed. Live save on blur.
//
// Collapsible by default so the drawer doesn't feel heavier for
// operators who don't need it on every open.

import React from 'react';

const FIELDS = {
  health: [
    { key: 'height',            label: 'Height',              placeholder: `5'10"` },
    { key: 'weight',            label: 'Weight',              placeholder: '180 lbs' },
    { key: 'primary_doctor',    label: 'Primary doctor',      placeholder: `Dr. Smith — Family Practice` },
    { key: 'preferred_hospital',label: 'Preferred hospital',  placeholder: 'St. Mary\'s' },
    { key: 'specialists',       label: 'Specialists',         placeholder: 'Cardiologist, dermatologist' },
    { key: 'prescriptions',     label: 'Prescriptions',       placeholder: 'Lisinopril 10mg, Metformin 500mg', textarea: true },
    { key: 'conditions',        label: 'Medical conditions',  placeholder: 'Hypertension, type-2 diabetes',      textarea: true },
    { key: 'hospitalizations',  label: 'Hospitalizations',    placeholder: 'None in last 5 years' },
    { key: 'household_size',    label: 'Family size',         placeholder: '4', numeric: true },
    { key: 'household_income',  label: 'Household income',    placeholder: '75000', numeric: true, prefix: '$' },
    { key: 'current_carrier',   label: 'Current carrier',     placeholder: 'BCBS' },
    { key: 'current_deductible',label: 'Current deductible',  placeholder: '2500', numeric: true, prefix: '$' },
    { key: 'dental',            label: 'Dental',              placeholder: 'None / Delta Dental' },
    { key: 'vision',            label: 'Vision',              placeholder: 'None / VSP' },
  ],
  life: [
    { key: 'smoker',            label: 'Smoker',              placeholder: 'no / occasional / daily' },
    { key: 'nicotine_use',      label: 'Nicotine use (any form)', placeholder: 'none / vape / chew' },
    { key: 'beneficiaries',     label: 'Beneficiaries',       placeholder: 'Spouse (100%)', textarea: true },
    { key: 'children_count',    label: 'Children',            placeholder: '2', numeric: true },
    { key: 'mortgage_balance',  label: 'Mortgage balance',    placeholder: '320000', numeric: true, prefix: '$' },
    { key: 'income',            label: 'Annual income',       placeholder: '85000',  numeric: true, prefix: '$' },
    { key: 'debt',              label: 'Other debt',          placeholder: '30000',  numeric: true, prefix: '$' },
    { key: 'assets',            label: 'Assets',              placeholder: '150000', numeric: true, prefix: '$' },
    { key: 'coverage_needed',   label: 'Coverage needed',     placeholder: '500000', numeric: true, prefix: '$' },
    { key: 'existing_policies', label: 'Existing policies',   placeholder: '$100k term with State Farm, 8 yrs left', textarea: true },
  ],
  medicare: [
    { key: 'part_a_effective',  label: 'Part A effective',    placeholder: '2020-01-01', type: 'date' },
    { key: 'part_b_effective',  label: 'Part B effective',    placeholder: '2020-01-01', type: 'date' },
    { key: 'current_carrier',   label: 'Current carrier',     placeholder: 'Humana' },
    { key: 'current_plan',      label: 'Current plan',        placeholder: 'MA Plus PPO 2026' },
    { key: 'lis',               label: 'LIS status',          placeholder: 'no / applied / full' },
    { key: 'medicaid',          label: 'Medicaid',            placeholder: 'no / yes / QMB' },
    { key: 'doctors',           label: 'Doctors',             placeholder: 'Dr. Smith — cardiology', textarea: true },
    { key: 'drugs',             label: 'Drugs',               placeholder: 'Eliquis, Metoprolol',    textarea: true },
  ],
};

const VERTICAL_TO_TAB = { health: 'health', life: 'life', medicare: 'medicare', senior: 'medicare' };

function Field({ f, value, onChange }) {
  const [local, setLocal] = React.useState(value ?? '');
  React.useEffect(() => { setLocal(value ?? ''); }, [value]);

  const commit = () => {
    if (String(local) === String(value ?? '')) return;
    onChange(f.numeric ? (local === '' ? null : Number(local)) : local);
  };

  const isTextarea = f.textarea;
  const commonProps = {
    value: local,
    onChange: (e) => setLocal(e.target.value),
    onBlur: commit,
    placeholder: f.placeholder,
    style: {
      width: '100%',
      background: '#ffffff',
      border: '1px solid #cbd5e1',
      borderRadius: 6,
      padding: '6px 8px',
      fontSize: 12,
      color: '#0f172a',
    },
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <div style={{ fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#64748b', fontWeight: 700 }}>
        {f.label}
      </div>
      {isTextarea ? (
        <textarea {...commonProps} rows={2} style={{ ...commonProps.style, resize: 'vertical', minHeight: 44 }}/>
      ) : (
        <div style={{ position: 'relative', display: 'flex' }}>
          {f.prefix && (
            <span style={{
              display: 'inline-flex', alignItems: 'center',
              background: '#f1f5f9', border: '1px solid #cbd5e1', borderRight: 'none',
              borderRadius: '6px 0 0 6px', padding: '0 8px', color: '#64748b', fontSize: 12,
            }}>{f.prefix}</span>
          )}
          <input
            type={f.type || 'text'}
            {...commonProps}
            style={{ ...commonProps.style, borderRadius: f.prefix ? '0 6px 6px 0' : 6 }}
          />
        </div>
      )}
    </div>
  );
}

export default function LeadInsuranceProfilePanel({ lead, onEdit, onSetField }) {
  const defaultTab = VERTICAL_TO_TAB[(lead?.vertical || 'health').toLowerCase()] || 'health';
  const [tab, setTab] = React.useState(defaultTab);
  const [expanded, setExpanded] = React.useState(false);

  const profile = lead?.insurance_profile || {};
  const activeProfile = profile[tab] || {};
  const activeFields = FIELDS[tab] || [];

  // Field count for the "3/14 filled" indicator on the collapsed strip.
  const filledCount = activeFields.filter(f => {
    const v = activeProfile[f.key];
    return v != null && v !== '';
  }).length;

  const setField = (key, value) => {
    const nextProfile = {
      ...profile,
      [tab]: { ...activeProfile, [key]: value },
    };
    // `onSetField` is a generic patch dispatcher wired to setLeadField
    // in CommissionTracker. Falling back to `onEdit` keeps the panel
    // renderable in isolation (Storybook / tests) but production ships
    // with onSetField.
    if (onSetField) onSetField({ insurance_profile: nextProfile });
    else if (onEdit) onEdit({ ...lead, insurance_profile: nextProfile });
  };

  return (
    <div style={{
      background: '#f8fafc',
      border: '1px solid #e2e8f0',
      borderRadius: 10,
      padding: 12,
    }}>
      <button
        onClick={() => setExpanded(v => !v)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#0891b2', fontWeight: 800 }}>
            Insurance Profile
          </div>
          <div style={{ fontSize: 11, color: '#64748b' }}>
            {filledCount}/{activeFields.length} filled · {tab}
          </div>
        </div>
        <div style={{ fontSize: 11, color: '#0891b2', fontWeight: 700 }}>
          {expanded ? 'Hide' : 'Edit'}
        </div>
      </button>

      {expanded && (
        <div style={{ marginTop: 12 }}>
          {/* Vertical tabs */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 12, borderBottom: '1px solid #e2e8f0' }}>
            {['health', 'life', 'medicare'].map(t => (
              <button
                key={t}
                onClick={() => setTab(t)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  borderBottom: `2px solid ${tab === t ? '#0891b2' : 'transparent'}`,
                  color: tab === t ? '#0f172a' : '#64748b',
                  padding: '6px 14px',
                  fontSize: 12,
                  fontWeight: tab === t ? 800 : 600,
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                }}
              >{t}</button>
            ))}
          </div>

          {/* Fields grid */}
          <div style={{
            display: 'grid', gap: 10,
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          }}>
            {activeFields.map(f => (
              <div key={f.key} style={{ gridColumn: f.textarea ? '1 / -1' : undefined }}>
                <Field
                  f={f}
                  value={activeProfile[f.key]}
                  onChange={(v) => setField(f.key, v)}
                />
              </div>
            ))}
          </div>

          <div style={{ fontSize: 10, color: '#64748b', marginTop: 10, fontStyle: 'italic' }}>
            Saved automatically. Fields flow into the AI briefing next time
            the drawer opens.
          </div>
        </div>
      )}
    </div>
  );
}
